import WS from 'webstudio/src/client/facade';
import {
  ICadCone,
  ICadCylinder,
  ICadFreeform,
  ICadItem,
  ICadModel,
  ICadPlane,
  ICadSphere,
  ICadTorus,
  ICadTruncatedCone,
  ICadUndefined,
  IDistanceMapMeasure,
  IDistanceMeasure,
  IGroupableItem,
  IGroupItem,
  IModelItem,
  IPhotoSetItem,
  IPointCloudScanItem,
  IProjectContent,
  ISectionGroupMeasure,
  ISimpleScanItem,
  ObjectItemType,
} from 'webstudio/src/client/project_content_types';
import { EScannerKind } from '_data/types/Object3d';
import { Hex, Pixel, URL, Uuid } from '_data/types/base';
import { IImportEvents } from '_helpers/processStatuses';

export interface IMeasurements {
  lines: IDistanceMeasure[];
  sections: ISectionGroupMeasure[];
  distanceMaps: IDistanceMapMeasure[];
  hasData?: boolean;
}

export interface IDistanceMapData {
  curve: number[];
  histogram: number[];
  ticks: number[];
}

export enum EScanDisplayMode {
  Triangles = WS.SCM_TRIANGLES,
  HighSurfels = WS.SCM_SURFELS_HIGH,
  MidSurfels = WS.SCM_SURFELS_MID,
}

export interface ILoadingProgressLevels {
  '0': number;
  '1': number;
}

export type ILoadingProgressObject = Record<string, ILoadingProgressLevels>;

export enum EBackfaceMode {
  None = WS.BM_NONE,
  Transparent = WS.BM_TRANSPARENT,
  Diffuse = WS.BM_DIFFUSE,
  Frontface = WS.BM_FRONTFACE,
}

type PhotoViewX = Pixel;
type PhotoViewY = Pixel;
type PhotoViewHeight = Pixel;
type PhotoViewWidth = Pixel;
export type IPhotoViewRectangleData = [PhotoViewX, PhotoViewY, PhotoViewWidth, PhotoViewHeight];

export interface IPhotoViewRectangleEvent {
  rect: IPhotoViewRectangleData;
  photoGroupId: string;
  photoId: string;
}

interface ITypedScanner {
  scanner?: EScannerKind;
}

type IWSModelItem = Omit<IModelItem, 'scanner'> & ITypedScanner;
type IWSSimpleScanItem = Omit<ISimpleScanItem, 'scanner'> & ITypedScanner;
type IWSPointCloudScanItem = Omit<IPointCloudScanItem, 'scanner'> & ITypedScanner;

interface IWSObjectExtraUiFields {
  ui_alignGroupId?: string;
  ui_alignGroupColor?: string;
  extra: Record<string, any>;
  preview_url: URL;
  ui_order: number;
}

export type ICadModelWithExtraUiFields = ICadModel & IWSObjectExtraUiFields;
export type ICadCylinderWithExtraUiFields = ICadCylinder & IWSObjectExtraUiFields;
export type ICadConeWithExtraUiFields = ICadCone & IWSObjectExtraUiFields;
export type ICadTruncatedConeWithExtraUiFields = ICadTruncatedCone & IWSObjectExtraUiFields;
export type ICadSphereWithExtraUiFields = ICadSphere & IWSObjectExtraUiFields;
export type ICadPlaneWithExtraUiFields = ICadPlane & IWSObjectExtraUiFields;
export type ICadTorusWithExtraUiFields = ICadTorus & IWSObjectExtraUiFields;
export type ICadFreeformWithExtraUiFields = ICadFreeform & IWSObjectExtraUiFields;
export type ICadUndefinedWithExtraUiFields = ICadUndefined & IWSObjectExtraUiFields;

export type IGroupItemWithUiFields = IGroupItem & IWSObjectExtraUiFields;
export type IWSModelItemWithUiFields = IWSModelItem & IWSObjectExtraUiFields;
export type IWSSimpleScanItemWithUiFields = IWSSimpleScanItem & IWSObjectExtraUiFields;
export type IWSPointCloudScanItemWithUiFields = IWSPointCloudScanItem & IWSObjectExtraUiFields;
export type ICadItemWithUiFields =
  | ICadModelWithExtraUiFields
  | ICadCylinderWithExtraUiFields
  | ICadConeWithExtraUiFields
  | ICadTruncatedConeWithExtraUiFields
  | ICadSphereWithExtraUiFields
  | ICadPlaneWithExtraUiFields
  | ICadTorusWithExtraUiFields
  | ICadFreeformWithExtraUiFields
  | ICadUndefinedWithExtraUiFields;
export type IPhotoSetItemWithUiFields = IPhotoSetItem & IWSObjectExtraUiFields;

export type IWSObjectItem =
  | IWSModelItem
  | IWSSimpleScanItem
  | IWSPointCloudScanItem
  | IGroupItem
  | ICadItem
  | IPhotoSetItem;

// this object we use for duplication/group/import purposes
export interface IWSStubObject extends IGroupableItem, Pick<IWSObjectExtraUiFields, 'ui_order'> {
  projectId: number;
  color: Hex;
  type: ObjectItemType;
  forHide: Uuid[];
  isStub: boolean;
  reason?: IImportEvents;
}

export type IWSObjectItemWithUiFields =
  | IGroupItemWithUiFields
  | IWSModelItemWithUiFields
  | IWSSimpleScanItemWithUiFields
  | IWSPointCloudScanItemWithUiFields
  | ICadItemWithUiFields
  | IPhotoSetItemWithUiFields
  | IWSStubObject;

export type IWSProjectContent = Omit<IProjectContent, 'clone' | 'objects'> & {
  objects: IWSObjectItem[];
};
